import styled, { css } from 'styled-components';
import {
    BorderRadiusSmall,
    ColorNeutralBlack,
    ColorNordicGrey3,
    Spacing4,
    Spacing8
} from 'autogen/design-tokens/tokens';
import type { MiniBasketLineViewModel } from 'autogen/swagger/all';
import {
    GetIsMatasPlusOfferNudgeActiveQueryKey,
    GetProductInformationQueryKey,
    useDeleteFromBasketMutation,
    useTogglePlusSignupMutation
} from 'autogen/swagger/all/hooks';
import { ButtonWithIcon } from 'components/Button';
import NumberInput from 'components/basket/BasketItem/components/Actions/components/NumberInput';
import SubscriptionInformation from 'components/basket/BasketItem/components/Subscription/components/SubscriptionInformation';
import { PriceContainer } from 'components/basket/BasketItem/shared/styles';
import { updateBasket } from 'components/basket/shared/basket';
import Card from 'components/generic/card';
import HighlightedText from 'components/generic/HighlightedText';
import ProductImage from 'components/generic/productImage';
import { ProductImageContainerLink } from 'components/generic/productImage/shared/styles';
import ProductName from 'components/generic/productName';
import { Price } from 'components/generic/productPrice';
import PriceWithDiscount from 'components/generic/productPrice/components/PriceWithDiscount';
import Icon from 'components/Icon';
import Logo from 'components/Icon/Logo';
import Flex from 'components/Layout/components/Flex';
import { OTCSplash } from 'components/product/components/OTCNotification/OTCNotification';
import { useGetProductListPartial } from 'components/shared/productList/shared/hooks/useGetProductListPartialQuery';
import track from 'modules/tracking/dataLayer';
import queryClient from 'setup/react-query/queryClient';
import ThemeProvider from 'theming/ThemeProvider';
import { breakpoint, sizes } from 'variables';
import MiniBasketDiscountInfo from './MiniBasketDiscountInfo';

/* #region styles */
export const Grid = styled.div`
    display: grid;
    gap: ${Spacing8};
    grid-template-columns: auto 64px 1fr;
    align-items: center;

    ${breakpoint.up(sizes.lg)} {
        grid-template-columns: auto 64px 1fr;
    }
`;

const StyledOTCSplash = styled(OTCSplash)`
    position: absolute;
    right: 0;
    bottom: ${Spacing4};
`;

const StyledNumberInput = styled(NumberInput)`
    background-color: white;
    border: 1px solid ${ColorNordicGrey3};
    border-radius: ${BorderRadiusSmall};
    width: 64px;
    min-width: 64px;
`;

const commonPriceStyling = css`
    white-space: nowrap;
    width: 76px;
    align-items: end;
`;

const StyledPriceWithDiscount = styled(PriceWithDiscount)`
    ${commonPriceStyling}
`;

const StyledPrice = styled(Price)`
    ${commonPriceStyling}
`;

const LogoContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 64px;
    height: 64px;
    padding: ${({ theme }) => theme.Spacing4};
    border: 1px solid ${({ theme }) => theme.ColorBorderPrimary};
    background: ${({ theme }) => theme.ColorBackgroundPrimary};
    border-radius: ${({ theme }) => theme.RadiusSmall};
`;

/* #endregion */

export default function MiniBasketItem({ model }: { model: MiniBasketLineViewModel }) {
    const {
        productLinkAction,
        productName,
        otcWarning,
        image,
        priceInfo,
        productId,
        quantity,
        maximumSalesQuantity,
        minimumSalesQuantity,
        discountInfo,
        subscriptionInfo,
        isMatasPlus
    } = model;
    const { refetch: refetchProductList } = useGetProductListPartial();
    const { mutateAsync: togglePlus } = useTogglePlusSignupMutation({
        onSuccess: (response) => {
            updateBasket();
            response.dataLayerDto && track(response.dataLayerDto);
            queryClient.invalidateQueries({ queryKey: [GetProductInformationQueryKey] });
            queryClient.invalidateQueries({ queryKey: [GetIsMatasPlusOfferNudgeActiveQueryKey] });
            refetchProductList();
        }
    });

    const { mutateAsync: deleteFromBasket } = useDeleteFromBasketMutation({
        onSuccess: (response) => {
            updateBasket();
            response.dataLayerDto && track(response.dataLayerDto);
        }
    });

    function productImageRenderer() {
        if (isMatasPlus) {
            return (
                <ThemeProvider theme="club2">
                    <LogoContainer>
                        <Logo type="clubPlusVertical" />
                    </LogoContainer>
                </ThemeProvider>
            );
        }

        return (
            <ProductImageContainerLink action={productLinkAction}>
                <ProductImage width={64} image={image} transformType="SquareScale" />
                {otcWarning && <StyledOTCSplash model={otcWarning} size="sm" />}
            </ProductImageContainerLink>
        );
    }

    return (
        <Card size={null}>
            <Card.Content>
                <Grid>
                    <ButtonWithIcon
                        css={css`
                            align-self: center;
                        `}
                        size="sm"
                        variant="ghostOnLight"
                        icon={<Icon type="Close" size={16} />}
                        onClick={() =>
                            isMatasPlus
                                ? togglePlus({ params: { enable: false } })
                                : deleteFromBasket({ params: { productId } })
                        }
                    />

                    {productImageRenderer()}

                    <Flex.Row justifyContent="space-between" alignItems="center">
                        <Flex.Column gap={Spacing8}>
                            {discountInfo && <MiniBasketDiscountInfo model={discountInfo} />}
                            <ProductName
                                productName={productName}
                                size="sm"
                                customTheme={{
                                    ProductTitle: css`
                                        color: ${ColorNeutralBlack};
                                    `
                                }}
                                strongProductTitle={false}
                            />
                            {subscriptionInfo && (
                                <HighlightedText withBorder={false}>
                                    <Flex.Column gap={Spacing4}>
                                        <SubscriptionInformation model={subscriptionInfo} productId={productId} />
                                    </Flex.Column>
                                </HighlightedText>
                            )}
                        </Flex.Column>

                        <StyledNumberInput
                            size="sm"
                            productId={productId}
                            maximumSalesQuantity={maximumSalesQuantity}
                            minimumSalesQuantity={minimumSalesQuantity}
                            quantity={quantity}
                            autoFocus={false}
                        />

                        <PriceContainer gap={Spacing8} alignItems="end">
                            {priceInfo.hasDiscount ? (
                                <StyledPriceWithDiscount
                                    size={12}
                                    price={priceInfo.subtotal}
                                    priceBefore={priceInfo.subtotalBefore}
                                />
                            ) : (
                                <StyledPrice size={12} price={priceInfo.subtotal} />
                            )}

                            {/* <LinkedItemPrices model={linkedItems} /> */}
                        </PriceContainer>
                    </Flex.Row>
                </Grid>
            </Card.Content>
        </Card>
    );
}
